import React, { FC } from 'react';
import { Product } from 'shared/types/product/Product';

import Slider from 'components/commercetools-ui/atoms/slider';
import Typography from 'components/commercetools-ui/atoms/typography';
import { desktop, minitablet, mobile, smallMobile, tablet } from 'helpers/utils/screensizes';
import useMediaQuery from 'helpers/hooks/useMediaQuery';
import ProductTile, { type ProductTileLayout } from 'components/commercetools-ui/organisms/product/product-tile';

import { SectionWithProductSliderContent } from './types';
import { useAdditionalCss } from '../helpers/useAdditionalCss';
import { useVisibleSlides } from '../helpers/useVisibleSlides';
import { SECTION_SPACING } from '../helpers/rendering';

/**
 * Hoisted so the same map drives both the carousel and the decision about whether arrows are any
 * use: they have to agree, and they stop agreeing the moment one of them is edited alone.
 */
const SLIDES_PER_VIEW = 2.1;
const SLIDER_BREAKPOINTS = {
  [smallMobile]: { slidesPerView: 1.2, slidesPerGroup: 1, spaceBetween: 16 },
  [mobile]: { slidesPerView: 2.1, slidesPerGroup: 2, spaceBetween: 16 },
  [tablet]: { slidesPerView: 4, slidesPerGroup: 4, spaceBetween: 16 },
};

/**
 * The "Algolia Query" carousel, rendered from the server-preloaded products.
 *
 * The products arrive in the index's own order, which is new-first on both products indices — the
 * client-side `sortHitsByIsNew` pass this used to run re-sorted them into the order they already had,
 * so it is gone along with the `searchableAttributes.IsNew` field it needed.
 */
export const AlgoliaQuerySliderView: FC<{
  content: SectionWithProductSliderContent;
  products: Product[];
  tileLayout: ProductTileLayout;
}> = ({ content, products, tileLayout }) => {
  const [isMiniTabletSize] = useMediaQuery(minitablet);
  const [isTabletSize] = useMediaQuery(tablet);
  const [isDesktopSize] = useMediaQuery(desktop);

  const visibleSlides = useVisibleSlides(SLIDES_PER_VIEW, SLIDER_BREAKPOINTS);

  const title = content?.title;
  const additional_css = content?.additional_css;
  const additional_css_class_names = content?.additional_css_class_names;

  const { uniqueClass, scopedAdditionalCss } = useAdditionalCss(additional_css, 'AlgoliaQueryProductSlider');

  return (
    <>
      {scopedAdditionalCss && <style dangerouslySetInnerHTML={{ __html: scopedAdditionalCss }} />}
      <div className={`${SECTION_SPACING} bg-[#E5E9EB] px-24 py-24 md:px-28 lg:px-48 lg:py-40 xl:px-60 2xl:px-80 2xl:py-60 ${[additional_css_class_names, uniqueClass].filter(Boolean).join(' ')}`}>
        <div id="algolia-product-slider">
          {title && (
            <Typography className="pb-32 text-32 font-medium text-primary-black xl:text-36 2xl:text-48" as="h3">
              {title}
            </Typography>
          )}
          <div className="product-slider-container relative w-full">
            <Slider
              className="mb-32"
              slidesPerView={SLIDES_PER_VIEW}
              slidesPerGroup={1}
              dots={false}
              autoplay={false}
              // Arrows only when the result set overflows what is on screen -- see product-ids.tsx.
              arrows={products.length > visibleSlides && (isTabletSize || isDesktopSize || isMiniTabletSize)}
              innerArrows
              solidArrows
              allowArrowsOnTouchDevice
              nextButtonStyles={{ top: '100%', left: 50 }}
              prevButtonStyles={{ top: '100%', left: 0 }}
              allowTouchMove
              spaceBetween={8}
              breakpoints={SLIDER_BREAKPOINTS}
            >
              {products.map((product, index) => (
                <div key={product.productId ?? product.slug ?? index} className="mb-48">
                  <ProductTile product={product} layout={tileLayout} />
                </div>
              ))}
            </Slider>
          </div>
        </div>
      </div>
    </>
  );
};
